import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut,
  Loader2,
  Clock,
  MapPin,
  ChevronRight,
  Plus,
  CheckCircle2,
  FileText,
  Star,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useHomeownerAuth } from "@/context/HomeownerAuthContext";
import MessageBoard from "@/components/MessageBoard";
import UnreadBadge from "@/components/UnreadBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  open: { label: "Open", color: "bg-[#0E93EC] text-white" },
  estimating: { label: "Estimating", color: "bg-[#0E93EC] text-white" },
  accepted: { label: "Accepted", color: "bg-[#0E93EC] text-[#EEEEEF]" },
  in_progress: { label: "In progress", color: "bg-[#0E93EC] text-[#EEEEEF]" },
  completed: { label: "Completed", color: "bg-[#EEEEEF]/15 text-white" },
  canceled: { label: "Canceled", color: "bg-[#C9C6C6]/30 text-white" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "";
  return "$" + Number(n).toLocaleString();
}

/* ============================================================
 *  Estimate row (contractor's bid) with Accept action
 * ============================================================ */
function EstimateRow({ estimate, canAccept, onAccept, busy }) {
  const statusMap = {
    sent: { label: "Awaiting your decision", color: "text-[#0E93EC]" },
    accepted: { label: "You accepted this bid", color: "text-[#0E93EC]" },
    declined: { label: "Declined", color: "text-[#C9C6C6]" },
    withdrawn: { label: "Withdrawn", color: "text-[#C9C6C6]" },
  };
  const meta = statusMap[estimate.status] || statusMap.sent;
  return (
    <div
      className="border border-[#C9C6C6]/10 rounded-xl bg-[#0D1421] p-4"
      data-testid={`estimate-${estimate.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
            {estimate.contractor_business || "Local Pro"}
          </div>
          <div className="font-editorial text-2xl text-[#EEEEEF] tracking-tighter mt-0.5">
            {fmtMoney(estimate.price_low)}
            {Number(estimate.price_high) > Number(estimate.price_low) && (
              <>
                <span className="text-[#C9C6C6]"> – </span>
                {fmtMoney(estimate.price_high)}
              </>
            )}
          </div>
          {estimate.items && estimate.items.length > 0 && (
            <div className="mt-3 space-y-1 border border-[#C9C6C6]/10 rounded-lg p-3 bg-[#010204]"
              data-testid={`estimate-items-${estimate.id}`}>
              {estimate.items.map((it, idx) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span className="text-[#EEEEEF]">{it.description} <span className="text-[#C9C6C6]">× {it.qty}</span></span>
                  <span className="text-[#EEEEEF]">{fmtMoney(it.line_total)}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs pt-1 border-t border-[#C9C6C6]/10 font-semibold">
                <span className="text-[#C9C6C6] uppercase tracking-[0.15em] text-[10px]">Total</span>
                <span className="text-[#0E93EC]">{fmtMoney(estimate.price_low)}</span>
              </div>
            </div>
          )}
          {estimate.estimated_days ? (
            <div className="mt-1 text-xs text-[#C9C6C6]">
              ~{estimate.estimated_days} day{estimate.estimated_days === 1 ? "" : "s"} of work
            </div>
          ) : null}
          {estimate.contractor_id && (
            <Link
              to={`/pro/${estimate.contractor_id}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0E93EC] hover:text-[#010204]"
              data-testid={`estimate-view-pro-${estimate.id}`}
            >
              View pro profile →
            </Link>
          )}
        </div>
        <div className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${meta.color}`}>
          {meta.label}
        </div>
      </div>
      {estimate.scope && (
        <p className="mt-3 text-sm text-[#EEEEEF] whitespace-pre-wrap leading-relaxed">
          {estimate.scope}
        </p>
      )}
      {canAccept && estimate.status === "sent" && (
        <div className="mt-4 flex gap-2">
          <Button
            onClick={() => onAccept(estimate.id)}
            disabled={busy}
            className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
            data-testid={`estimate-accept-${estimate.id}`}
          >
            Accept this bid
          </Button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
 *  Agreement + signoff panel for accepted jobs
 * ============================================================ */
function AgreementPanel({ job, viewerRole = "homeowner", onSignedoff, refetch, invoice }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const homeownerSigned = job.agreement_homeowner_signed;
  const contractorSigned = job.agreement_contractor_signed;
  const iSigned = viewerRole === "homeowner" ? homeownerSigned : contractorSigned;
  const sign = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/jobs/${job.id}/signoff`,
        { party: viewerRole },
        { withCredentials: true }
      );
      toast.success("Signed. Waiting on the other party if needed.");
      onSignedoff?.();
      refetch?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-[#C9C6C6]/10 rounded-xl bg-[#010204] p-4" data-testid={`agreement-${job.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#0E93EC]" />
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
            Project agreement · Job #{job.invoice_number}
          </div>
        </div>
        <button
          onClick={() => setShow((s) => !s)}
          className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C9C6C6] hover:text-[#EEEEEF] flex items-center gap-1"
          data-testid={`agreement-toggle-${job.id}`}
        >
          {show ? "Hide" : "View"}
          <ChevronDown className={`w-3 h-3 transition-transform ${show ? "rotate-180" : ""}`} />
        </button>
      </div>
      {show && job.agreement_text && (
        <pre className="mt-4 whitespace-pre-wrap font-mono-alt text-[12px] text-[#EEEEEF] leading-relaxed bg-[#0D1421] border border-[#C9C6C6]/10 rounded-xl p-4 max-h-[420px] overflow-y-auto">
          {job.agreement_text}
        </pre>
      )}
      {(job.schedule?.scheduled_date || invoice) && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {job.schedule?.scheduled_date && (
            <div className="p-3 border border-[#0E93EC]/30 bg-[#0E93EC]/5 rounded-lg" data-testid={`job-schedule-${job.id}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Scheduled</div>
              <div className="text-sm text-[#EEEEEF] font-medium">
                {job.schedule.scheduled_date}{job.schedule.time_window ? ` · ${job.schedule.time_window}` : ""}
              </div>
              {job.schedule.note && <div className="text-xs text-[#C9C6C6] mt-1">{job.schedule.note}</div>}
            </div>
          )}
          {invoice && (
            <div className="p-3 border border-[#C9C6C6]/10 rounded-lg" data-testid={`job-invoice-${job.id}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9C6C6] mb-1">Invoice {invoice.invoice_number}</div>
              <div className="text-sm text-[#EEEEEF] font-medium flex items-center gap-2">
                {fmtMoney(invoice.total)}
                <span className={`text-[9px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 ${invoice.status === "paid" ? "bg-[#0E2F5F] text-white border border-[#0E93EC]" : "bg-[#0E93EC] text-white"}`}>
                  {invoice.status === "paid" ? "Paid" : "Payment due"}
                </span>
              </div>
              {invoice.due_date && <div className="text-xs text-[#C9C6C6] mt-1">Due {invoice.due_date}</div>}
            </div>
          )}
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div
          className={`p-3 border ${homeownerSigned ? "border-[#0E93EC] bg-[#0E93EC]/5" : "border-[#C9C6C6]/10"}`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Homeowner</div>
          <div className="flex items-center gap-2 text-sm">
            {homeownerSigned ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#0E93EC]" />
                <span className="font-medium">Signed off</span>
              </>
            ) : (
              <span className="text-[#C9C6C6]">Not yet signed</span>
            )}
          </div>
        </div>
        <div
          className={`p-3 border ${contractorSigned ? "border-[#0E93EC] bg-[#0E93EC]/5" : "border-[#C9C6C6]/10"}`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Pro</div>
          <div className="flex items-center gap-2 text-sm">
            {contractorSigned ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#0E93EC]" />
                <span className="font-medium">Signed off</span>
              </>
            ) : (
              <span className="text-[#C9C6C6]">Not yet signed</span>
            )}
          </div>
        </div>
      </div>
      {!iSigned && job.status !== "completed" && (
        <Button
          onClick={sign}
          disabled={busy}
          className="mt-4 rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-11 px-6"
          data-testid={`agreement-sign-${job.id}`}
        >
          {busy ? "Signing…" : "Mark my side complete"}
        </Button>
      )}
    </div>
  );
}

/* ============================================================
 *  Review submission form (after completion)
 * ============================================================ */
function ReviewForm({ job, existingReview, onSubmitted }) {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [body, setBody] = useState(existingReview?.body || "");
  const [busy, setBusy] = useState(false);
  if (existingReview) {
    const statusLabel = {
      pending: "Pending moderation",
      approved: "Published",
      rejected: "Rejected by admin",
    }[existingReview.status] || "Submitted";
    return (
      <div className="border border-[#C9C6C6]/10 rounded-xl bg-[#0D1421] p-4" data-testid={`review-existing-${job.id}`}>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-2">
          Your review
        </div>
        <div className="flex items-center gap-1 mb-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className={`w-4 h-4 ${n <= existingReview.rating ? "fill-[#0E93EC] text-[#0E93EC]" : "text-[#C9C6C6]"}`}
            />
          ))}
        </div>
        <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap">{existingReview.body}</p>
        <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#C9C6C6]">
          {statusLabel}
        </div>
        {existingReview.status === "rejected" && existingReview.moderator_note && (
          <div className="mt-2 text-xs text-[#EEEEEF]">
            Admin note: {existingReview.moderator_note}
          </div>
        )}
      </div>
    );
  }
  const submit = async () => {
    if (!rating) {
      toast.error("Pick a rating");
      return;
    }
    if (!body.trim()) {
      toast.error("Write a short review");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/homeowner/reviews`,
        { job_id: job.id, rating, body: body.trim() },
        { withCredentials: true }
      );
      toast.success("Thanks — your review is queued for moderation");
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-[#0E93EC]/40 bg-[#0E93EC]/5 p-4" data-testid={`review-form-${job.id}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-2">
        Job complete — leave a review
      </div>
      <div className="flex items-center gap-1 mb-3" role="radiogroup" aria-label="rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => setRating(n)}
            className="p-1"
            data-testid={`review-star-${job.id}-${n}`}
            aria-label={`${n} star`}
          >
            <Star
              className={`w-6 h-6 transition-colors ${n <= rating ? "fill-[#0E93EC] text-[#0E93EC]" : "text-[#C9C6C6] hover:text-[#0E93EC]"}`}
            />
          </button>
        ))}
      </div>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="How was the experience? Was the work quality good? Would you hire again?"
        rows={3}
        className="bg-[#0D1421] border-[#C9C6C6]/10 rounded-none focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
        data-testid={`review-body-${job.id}`}
      />
      <Button
        onClick={submit}
        disabled={busy}
        className="mt-3 rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-11 px-6"
        data-testid={`review-submit-${job.id}`}
      >
        {busy ? "Submitting…" : "Submit review"}
      </Button>
    </div>
  );
}

/* ============================================================
 *  Job card
 * ============================================================ */
function JobCard({ item, onChanged }) {
  const meta = STATUS_META[item.status] || STATUS_META.open;
  const [unreadKey, setUnreadKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const acceptEstimate = async (estId) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/homeowner/estimates/${estId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success("Accepted. Job locked in — take a look at your agreement below.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-[#010204] border border-[#C9C6C6]/10 rounded-xl p-5 md:p-6"
      data-testid={`homeowner-job-${item.lead.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span
              className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 ${meta.color}`}
            >
              {meta.label}
            </span>
            {item.invoice_number && (
              <span className="text-[11px] font-mono-alt text-[#C9C6C6]">
                Job #{item.invoice_number}
              </span>
            )}
            {item.contractor && (
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#C9C6C6]">
                with <b className="text-[#EEEEEF]">{item.contractor.business_name}</b>
              </span>
            )}
            {item.job && ["accepted", "in_progress", "completed"].includes(item.status) && (
              <UnreadBadge jobId={item.job.id} refreshKey={unreadKey} />
            )}
          </div>
          <div className="font-editorial text-2xl text-[#EEEEEF] leading-tight">
            {item.lead.service_type}
          </div>
          {item.lead.project_details && (
            <p className="mt-1 text-sm text-[#C9C6C6] line-clamp-2 max-w-xl">
              {item.lead.project_details}
            </p>
          )}
          <div className="mt-3 flex items-center gap-4 text-xs text-[#C9C6C6]">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {item.lead.zip_code}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeAgo(item.lead.created_at)}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
            Quotes
          </div>
          <div className="text-sm font-medium">
            {item.estimates?.length || 0} received
          </div>
        </div>
      </div>

      {/* Estimates */}
      {item.estimates && item.estimates.length > 0 && (
        <div className="mt-5 space-y-3" data-testid={`estimates-${item.lead.id}`}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
            Bids ({item.estimates.length})
          </div>
          {item.estimates.map((e) => (
            <EstimateRow
              key={e.id}
              estimate={e}
              canAccept={item.status !== "accepted" && item.status !== "in_progress" && item.status !== "completed"}
              onAccept={acceptEstimate}
              busy={busy}
            />
          ))}
        </div>
      )}

      {/* Agreement + signoff */}
      {item.job && ["accepted", "in_progress", "completed"].includes(item.status) && (
        <div className="mt-5">
          <AgreementPanel
            job={item.job}
            viewerRole="homeowner"
            refetch={onChanged}
            invoice={item.invoice}
          />
        </div>
      )}

      {/* Message board — opens once estimate accepted */}
      {item.job && ["accepted", "in_progress", "completed"].includes(item.status) && (
        <div className="mt-5">
          <MessageBoard
            jobId={item.job.id}
            viewerRole="homeowner"
            jobStatus={item.status}
            onRead={() => setUnreadKey(Date.now())}
          />
        </div>
      )}

      {/* Review */}
      {item.status === "completed" && item.job && (
        <div className="mt-5">
          <ReviewForm
            job={item.job}
            existingReview={item.review}
            onSubmitted={onChanged}
          />
        </div>
      )}
    </div>
  );
}

export default function HomeownerDashboard() {
  const { user, loading: authLoading, logout } = useHomeownerAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/homeowner/jobs`, {
        withCredentials: true,
      });
      setJobs(data);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/homeowner/login" replace />;

  return (
    <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF]" data-testid="homeowner-dashboard">
      <header className="border-b border-[#C9C6C6]/8 bg-[#0D1421] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto shrink-0" data-testid="brand-logo-img" />
            <span className="hidden md:inline-block ml-3 text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6] border-l border-[#C9C6C6]/25 pl-3">
              Homeowner
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Link
              to="/homeowner/profile"
              className="hidden md:block text-right hover:opacity-70 transition-opacity"
              data-testid="dashboard-profile-link"
            >
              <div className="text-sm font-medium leading-tight">{user.name}</div>
              <div className="text-[11px] text-[#C9C6C6]">{user.email}</div>
            </Link>
            <Button
              onClick={logout}
              variant="ghost"
              className="rounded-none text-[#C9C6C6] hover:text-[#EEEEEF] gap-2 px-2 sm:px-4"
              data-testid="homeowner-logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold text-[#0E93EC] mb-3 sm:mb-4">
            — Your projects
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 md:gap-6 mb-8 sm:mb-10">
            <h1 className="font-editorial text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[0.95] break-word-safe">
              Every project<br />
              <span className="italic">in one place.</span>
            </h1>
            <Link
              to="/#get-quotes"
              className="group inline-flex self-start items-center bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-12 pl-5 sm:pl-6 pr-4 font-medium text-sm sm:text-base"
              data-testid="new-project-cta"
            >
              <Plus className="w-4 h-4 mr-2" /> New project request
              <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0E93EC] ml-3 transition-transform group-hover:translate-x-1">
                <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#C9C6C6] py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
            Loading your projects…
          </div>
        ) : jobs.length === 0 ? (
          <div className="border border-dashed border-[#C9C6C6]/20 p-12 text-center bg-[#0D1421]">
            <div className="font-editorial text-3xl text-[#EEEEEF] mb-2">No projects yet.</div>
            <div className="text-[#C9C6C6] mb-6">
              Submit your first request and start getting quotes from local pros.
            </div>
            <Link
              to="/#get-quotes"
              className="inline-flex items-center bg-[#0E93EC] text-white h-12 px-6 font-medium"
            >
              Get free quotes <ChevronRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="homeowner-jobs-list">
            {jobs.map((j) => (
              <JobCard key={j.lead.id} item={j} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
